/**
 * Offline visitor pass renderer (ADDENDUM 2026-09-14, Item A).
 *
 * Renders the STATIC guest QR (server raw_code — never a rotating VQR1 frame,
 * which goes stale within 60s) plus a text block into a single PNG via canvas,
 * fully client-side. The saved image is a snapshot: scan-time validation
 * (soft-cancel, expiry, ALREADY_INSIDE) remains authoritative server-side.
 *
 * Image requirements per spec:
 *  - QR ≥ 512px longest side, quiet zone ≥ 4 modules (we request qzone=6 from
 *    the same qrserver renderer the app already uses — see qrImageUrl),
 *  - dark-on-white, explicit white background (no dark-mode inversion),
 *  - text ≥ 14px-equivalent (we render 28-34px on a 720px-wide canvas),
 *  - printable at ≥ 5cm wide (720px @ ~150dpi ≈ 12cm; module size ample).
 *
 * Layout (polished 2026-09-14 after live feedback): the Vecindus logo sits at
 * the top, then the QR, then a measured text block (title emphasized, wrapped
 * detail lines with real line heights) and a footer with guaranteed breathing
 * room. The canvas height is COMPUTED from measured wrapped line boxes, so no
 * element can ever overlap the footer or wrap into the next block.
 *
 * No Vecindus API calls are made; the only network fetch is the QR image from
 * the same external renderer already used everywhere for inline QR display
 * (qrImageUrl). Flagged as an adjacent issue: bundling a local QR generator
 * would remove that dependency later.
 */

import { qrImageUrl } from '../hooks/usePersonalQrCode';

export interface VisitorPassData {
  /** Static raw_code string from the server (NOT a VQR1 payload). */
  qrCode: string;
  /** Headline of the text block (visitor name). */
  title?: string;
  /** Detail lines: Anfitrión, Destino, vigencia, etc. */
  lines?: string[];
  /** Google Maps URL (2026-09-15): a downloaded PNG can't carry a clickable
   *  hyperlink, so we embed a small QR code OF this URL in the pass image —
   *  scanning it opens the destination in Google Maps. */
  mapsUrl?: string;
}

const PAD = 48;
const CANVAS_W = 720;
const QR_SIZE = 512;
const LOGO_SIZE = 72;

const FONT_STACK = 'system-ui, -apple-system, "Segoe UI", sans-serif';
const font = (px: number, weight = 400) => `${weight} ${px}px ${FONT_STACK}`;

const TITLE_FONT = font(34, 700);
const TITLE_LINE_H = 46;
const DETAIL_FONT = font(28);
const DETAIL_LINE_H = 40;
const FOOTER_MAIN_FONT = font(28, 700);
const FOOTER_MAIN_LINE_H = 36;
const FOOTER_SUB_FONT = font(22);
const FOOTER_SUB_LINE_H = 30;

const GAP_LOGO_QR = 32;
const GAP_QR_TEXT = 30;
const GAP_TITLE_DETAILS = 18;
const GAP_DETAIL_LINES = 12;
const GAP_TEXT_FOOTER = 40;

// Maps QR block (2026-09-15): small QR encoding the Google Maps URL, drawn
// under the text block with a one-line caption. 120px keeps it scannable
// (qrserver qzone=2) without competing visually with the 512px access QR.
const MAPS_QR_SIZE = 120;
const GAP_MAPS_QR_CAPTION = 8;

/** A measured line box: text drawn vertically within [y, y + lineH). */
export interface LayoutLine {
  text: string;
  font: string;
  color: string;
  lineH: number;
  y: number;
}

interface TextBlock {
  lines: Omit<LayoutLine, 'y'>[];
  gapAfter: number;
}

/** Word-wrap `text` to `maxW` with `fnt`; returns the wrapped lines. */
function wrapText(ctx: CanvasRenderingContext2D, text: string, fnt: string, maxW: number): string[] {
  ctx.font = fnt;
  const words = text.split(/\s+/).filter(Boolean);
  const out: string[] = [];
  let cur = '';
  for (const w of words) {
    const test = cur ? `${cur} ${w}` : w;
    if (ctx.measureText(test).width > maxW && cur) {
      out.push(cur);
      cur = w;
    } else {
      cur = test;
    }
  }
  if (cur) out.push(cur);
  return out.length ? out : [''];
}

function blockHeight(blocks: TextBlock[]): number {
  return blocks.reduce((s, b) => s + b.lines.length * b.lines[0].lineH + b.gapAfter, 0);
}

/** Load the bundled Vecindus logo (same-origin public asset — no tainting).
 *  Resolves null when unavailable (vector fallback is drawn instead). */
function loadLogo(): Promise<HTMLImageElement | null> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = '/favicon.svg';
  });
}

/** Inline vector fallback matching public/favicon.svg (rounded tile + chevron). */
function drawVectorLogo(ctx: CanvasRenderingContext2D, x: number, y: number, s: number) {
  const k = s / 40;
  const r = 10 * k;
  ctx.fillStyle = '#36CCD8';
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + s - r, y);
  ctx.arcTo(x + s, y, x + s, y + r, r);
  ctx.lineTo(x + s, y + s - r);
  ctx.arcTo(x + s, y + s, x + s - r, y + s, r);
  ctx.lineTo(x + r, y + s);
  ctx.arcTo(x, y + s, x, y + s - r, r);
  ctx.lineTo(x, y + r);
  ctx.arcTo(x, y, x + r, y, r);
  ctx.closePath();
  ctx.fill();
  const pt = (px: number, py: number): [number, number] => [x + px * k, y + py * k];
  ctx.fillStyle = '#ffffff';
  ctx.beginPath();
  ctx.moveTo(...pt(20, 8));
  ctx.lineTo(...pt(6, 22));
  ctx.lineTo(...pt(12, 28));
  ctx.lineTo(...pt(20, 20));
  ctx.lineTo(...pt(28, 28));
  ctx.lineTo(...pt(34, 22));
  ctx.closePath();
  ctx.fill();
  ctx.globalAlpha = 0.4;
  ctx.beginPath();
  ctx.moveTo(...pt(20, 16));
  ctx.lineTo(...pt(12, 24));
  ctx.lineTo(...pt(16, 28));
  ctx.lineTo(...pt(20, 24));
  ctx.lineTo(...pt(24, 28));
  ctx.lineTo(...pt(28, 24));
  ctx.closePath();
  ctx.fill();
  ctx.globalAlpha = 1;
}

function loadQrImageData(text: string, size: number, qzone: number): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    // qrserver serves permissive CORS headers (the existing share flow already
    // fetch()es it) — needed so the canvas stays untainted for toBlob().
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('qr-load-failed'));
    img.src = `${qrImageUrl(text, size)}&qzone=${qzone}`;
  });
}

function loadQrImage(code: string): Promise<HTMLImageElement> {
  // qzone=6 quiet-zone modules (spec minimum is 4), 512px square.
  return loadQrImageData(code, QR_SIZE, 6);
}

/** Maps QR for the downloaded pass (2026-09-15). Best-effort: resolves null
 *  when the small QR cannot load — the pass still renders without it. */
function loadMapsQrImage(mapsUrl: string): Promise<HTMLImageElement | null> {
  return loadQrImageData(mapsUrl, MAPS_QR_SIZE, 2).catch(() => null);
}

function canvasToPngBlob(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) => {
    if (canvas.toBlob) {
      canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('toBlob-null'))), 'image/png');
      return;
    }
    // Legacy fallback: dataURL -> blob.
    try {
      const [, b64] = canvas.toDataURL('image/png').split(',');
      const bin = atob(b64);
      const bytes = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
      resolve(new Blob([bytes], { type: 'image/png' }));
    } catch (e) {
      reject(e instanceof Error ? e : new Error('dataURL-failed'));
    }
  });
}

export interface RenderPassOptions {
  /** Test/render override: draw this QR image instead of fetching qrserver. */
  qrImage?: HTMLImageElement;
  /** Test hook: receives every placed line box (plus logo/QR rects) for
   *  overlap assertions. Rects: {x,y,w,h,label}. */
  onLayout?: (rects: { x: number; y: number; w: number; h: number; label: string }[]) => void;
}

/** Render the pass PNG. Returns null when the QR image cannot be loaded/drawn
 *  (callers must then fall back to the plain static QR + screenshot caveat). */
export async function renderVisitorPassPng(data: VisitorPassData, opts: RenderPassOptions = {}): Promise<Blob | null> {
  if (!data?.qrCode) return null;
  try {
    const qrImg = opts.qrImage ?? (await loadQrImage(data.qrCode));
    const mapsImg = data.mapsUrl ? await loadMapsQrImage(data.mapsUrl) : null;
    const logo = await loadLogo();
    const title = (data.title || '').trim();
    const details = (data.lines || []).map((l) => (l || '').trim()).filter(Boolean);

    // ---- Measure phase (layout computed BEFORE sizing the canvas). ----
    const meas = document.createElement('canvas').getContext('2d');
    if (!meas) return null;
    const maxW = CANVAS_W - PAD * 2;

    const contentBlocks: TextBlock[] = [];
    if (title) {
      contentBlocks.push({
        lines: wrapText(meas, title, TITLE_FONT, maxW).map((text) => ({
          text, font: TITLE_FONT, color: '#0f172a', lineH: TITLE_LINE_H,
        })),
        gapAfter: GAP_TITLE_DETAILS,
      });
    }
    details.forEach((line, i) => {
      contentBlocks.push({
        lines: wrapText(meas, line, DETAIL_FONT, maxW).map((text) => ({
          text, font: DETAIL_FONT, color: '#334155', lineH: DETAIL_LINE_H,
        })),
        gapAfter: i < details.length - 1 ? GAP_DETAIL_LINES : 0,
      });
    });

    const footerBlocks: TextBlock[] = [
      {
        lines: [{ text: 'Vecindus · Pase de visitante', font: FOOTER_MAIN_FONT, color: '#0f172a', lineH: FOOTER_MAIN_LINE_H }],
        gapAfter: 8,
      },
      {
        lines: [{ text: 'Funciona sin datos · sujeto a vigencia', font: FOOTER_SUB_FONT, color: '#64748b', lineH: FOOTER_SUB_LINE_H }],
        gapAfter: 0,
      },
    ];

    const contentH = blockHeight(contentBlocks);
    const footerH = blockHeight(footerBlocks);
    const mapsH = mapsImg ? MAPS_QR_SIZE + GAP_MAPS_QR_CAPTION + FOOTER_SUB_LINE_H : 0;
    const canvasH =
      PAD + LOGO_SIZE + GAP_LOGO_QR + QR_SIZE + GAP_QR_TEXT +
      (contentH > 0 ? contentH + GAP_TEXT_FOOTER : 0) +
      (mapsH > 0 ? mapsH + GAP_TEXT_FOOTER : 0) + footerH + PAD;

    // ---- Draw phase. ----
    const canvas = document.createElement('canvas');
    canvas.width = CANVAS_W;
    canvas.height = canvasH;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    // Explicit white — never inherit theming/dark mode (spec hard requirement).
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, CANVAS_W, canvasH);
    ctx.textBaseline = 'middle';
    ctx.textAlign = 'center';

    const rects: { x: number; y: number; w: number; h: number; label: string }[] = [];
    let y = PAD;

    // Logo — bundled same-origin asset, vector fallback, always same size.
    if (logo && logo.width > 0) {
      ctx.drawImage(logo, (CANVAS_W - LOGO_SIZE) / 2, y, LOGO_SIZE, LOGO_SIZE);
    } else {
      drawVectorLogo(ctx, (CANVAS_W - LOGO_SIZE) / 2, y, LOGO_SIZE);
    }
    rects.push({ x: (CANVAS_W - LOGO_SIZE) / 2, y, w: LOGO_SIZE, h: LOGO_SIZE, label: 'logo' });
    y += LOGO_SIZE + GAP_LOGO_QR;

    // QR centered, dark-on-white.
    ctx.drawImage(qrImg, (CANVAS_W - QR_SIZE) / 2, y, QR_SIZE, QR_SIZE);
    rects.push({ x: (CANVAS_W - QR_SIZE) / 2, y, w: QR_SIZE, h: QR_SIZE, label: 'qr' });
    y += QR_SIZE + GAP_QR_TEXT;

    // Text block: title emphasized, then wrapped detail lines.
    for (const block of contentBlocks) {
      for (const ln of block.lines) {
        ctx.font = ln.font;
        ctx.fillStyle = ln.color;
        const baseY = y + ln.lineH / 2;
        ctx.fillText(ln.text, CANVAS_W / 2, baseY);
        rects.push({ x: PAD, y, w: CANVAS_W - PAD * 2, h: ln.lineH, label: `text:${ln.text.slice(0, 24)}` });
        y += ln.lineH;
      }
      y += block.gapAfter;
    }

    // Maps QR block (2026-09-15): small QR encoding the Google Maps URL so
    // the SAVED IMAGE can navigate — a PNG cannot contain a clickable link.
    if (mapsImg) {
      ctx.drawImage(mapsImg, (CANVAS_W - MAPS_QR_SIZE) / 2, y, MAPS_QR_SIZE, MAPS_QR_SIZE);
      rects.push({ x: (CANVAS_W - MAPS_QR_SIZE) / 2, y, w: MAPS_QR_SIZE, h: MAPS_QR_SIZE, label: 'maps-qr' });
      y += MAPS_QR_SIZE + GAP_MAPS_QR_CAPTION;
      ctx.font = FOOTER_SUB_FONT;
      ctx.fillStyle = '#64748b';
      ctx.fillText('Escanea este código para abrir el mapa', CANVAS_W / 2, y + FOOTER_SUB_LINE_H / 2);
      rects.push({ x: PAD, y, w: CANVAS_W - PAD * 2, h: FOOTER_SUB_LINE_H, label: 'text:maps-caption' });
      y += FOOTER_SUB_LINE_H + GAP_TEXT_FOOTER;
    }

    // Footer anchored to the bottom with guaranteed breathing room.
    const footerTop = canvasH - PAD - footerH;
    let fy = footerTop;
    for (const block of footerBlocks) {
      for (const ln of block.lines) {
        ctx.font = ln.font;
        ctx.fillStyle = ln.color;
        ctx.fillText(ln.text, CANVAS_W / 2, fy + ln.lineH / 2);
        rects.push({ x: PAD, y: fy, w: CANVAS_W - PAD * 2, h: ln.lineH, label: `footer:${ln.text.slice(0, 24)}` });
        fy += ln.lineH;
      }
      fy += block.gapAfter;
    }

    opts.onLayout?.(rects);
    return await canvasToPngBlob(canvas);
  } catch {
    return null;
  }
}

export type SavePassResult = 'shared' | 'downloaded' | 'unsupported' | 'cancelled';

/**
 * True on Apple touch devices (iPhone/iPad/iPod, or iPadOS 13+ masquerading as
 * Mac). On iOS Safari both save paths are unreliable when invoked after async
 * work: `navigator.share` requires fresh user activation (the canvas render's
 * image loads consume it -> NotAllowedError, nothing happens) and
 * `<a download>` on blob: URLs is ignored. Callers use this BEFORE awaiting
 * the render to route iOS straight to the long-press overlay.
 */
export function isIosDevice(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  if (/iPad|iPhone|iPod/.test(ua)) return true;
  // iPadOS 13+ reports as Mac with multi-touch.
  const nav = navigator as Navigator & { platform?: string; maxTouchPoints?: number };
  return nav.platform === 'MacIntel' && (nav.maxTouchPoints ?? 0) > 1;
}

function sanitizeNamePart(s: string): string {
  return (s || '')
    .trim()
    .split(/\s+/)[0]
    .replace(/[^A-Za-z0-9_\-.]/g, '')
    .slice(0, 40);
}

/** `Vecindus_Pase_<VisitorFirstName>_<YYYYMMDD>.png` (spec: deterministic so
 *  re-saving overwrites/versions naturally instead of littering). */
export function passFileName(visitorName: string, now = new Date()): string {
  const first = sanitizeNamePart(visitorName) || 'Visitante';
  const p = (n: number) => String(n).padStart(2, '0');
  return `Vecindus_Pase_${first}_${now.getFullYear()}${p(now.getMonth() + 1)}${p(now.getDate())}.png`;
}

/** Save the rendered PNG: navigator.share with files when supported,
 *  else `<a download>`, else 'unsupported' (caller shows long-press hint). */
export async function savePassImage(blob: Blob, visitorName: string): Promise<SavePassResult> {
  const fname = passFileName(visitorName);
  // 1) Native share sheet with the file (Android Chrome, modern iOS Safari).
  try {
    if (typeof navigator !== 'undefined' && navigator.share && typeof navigator.canShare === 'function') {
      const file = new File([blob], fname, { type: 'image/png' });
      const shareData: ShareData = { files: [file], title: 'Pase de visitante Vecindus' };
      if (navigator.canShare(shareData)) {
        await navigator.share(shareData);
        return 'shared';
      }
    }
  } catch (e: any) {
    // User dismissed the share sheet — not an error, do not fall through to a
    // surprise download on top of the sheet.
    if (e && e.name === 'AbortError') return 'cancelled';
    if (e && e.name === 'NotAllowedError') return 'unsupported'; // activation expired (iOS)
    // Other share errors: continue to download fallback.
  }
  // 2) <a download> (desktop browsers, Android Chrome w/o share support).
  try {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fname;
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 10000);
    return 'downloaded';
  } catch {
    return 'unsupported';
  }
}
